import { useCallback, useState, useMemo } from 'react';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { useAllVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { useVaultManagerOptions } from '../../../hooks/vaults/useVaultManagerOptions';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { SortableHeaderType } from 'src/components/Vaults/type';
import SideDrawer from '../BaseDrawers/SideDrawer/SideDrawer';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import Button from 'src/components/primitives/Button/Button';
import { PeriodApy } from 'src/@types/vaults';

const VaultDisplayFiltersDrawer = () => {
	const handleCloseDrawer = useDrawersStore((s) => s.handleCloseDrawer);
	const { getVaultDisplaySettings, setVaultDisplaySettings, activeTab } =
		useVaultsStore();
	const { data } = useAllVaultStats();
	const vaultsStatsList = data.list;
	const currentSettings = getVaultDisplaySettings();
	const spotMarketLookup = useSpotMarketsLookup();

	// Local state to track changes before applying
	const [settings, setSettings] = useState<{
		targetVaultManager: string;
		targetDepositAsset: string; // Allow both string and number
		targetApyPeriod: PeriodApy;
		sortedBy: SortableHeaderType;
	}>(currentSettings);

	const vaultManagerOptions = useVaultManagerOptions(activeTab);

	// Get deposit asset options
	const depositAssetOptions = useMemo(
		() => [
			{ value: 'All', label: 'All Assets' },
			...Array.from(
				new Set(
					vaultsStatsList.map(
						(vaultStats) => vaultStats.uiVaultConfig.depositAsset
					)
				)
			).map((asset) => ({
				value: String(spotMarketLookup[asset].marketIndex),
				label: spotMarketLookup[asset].symbol,
			})),
		],
		[vaultsStatsList]
	);

	const apyOptions: { value: PeriodApy; label: string }[] = [
		{ value: '7d', label: '7D' },
		{ value: '30d', label: '30D' },
		{ value: '90d', label: '90D' },
	];

	const handleApply = useCallback(() => {
		setVaultDisplaySettings((draft) => {
			draft.targetVaultManager = settings.targetVaultManager;
			draft.targetDepositAsset = settings.targetDepositAsset;
			draft.targetApyPeriod = settings.targetApyPeriod;
			draft.sortedBy = settings.sortedBy;
		});
		handleCloseDrawer('vaultDisplayFilters');
	}, [settings, handleCloseDrawer, setVaultDisplaySettings]);

	const handleCancel = useCallback(() => {
		setSettings(currentSettings); // Reset to current settings
		handleCloseDrawer('vaultDisplayFilters');
	}, [currentSettings, handleCloseDrawer]);

	return (
		<SideDrawer.Container
			drawerName="vaultDisplayFilters"
			onClose={handleCancel}
		>
			<SideDrawer.Title>Filters</SideDrawer.Title>
			<SideDrawer.Body>
				<div className="flex flex-col gap-sm">
					<label className="typo-label-xs text-text-secondary">Manager</label>
					<RadioGroup.Default
						variant="card"
						size="md"
						orientation="horizontal"
						className="flex-wrap gap-sm"
						items={vaultManagerOptions.map((option) => ({
							...option,
							id: option.value,
						}))}
						value={settings.targetVaultManager}
						onValueChange={(value) =>
							setSettings((prev) => ({
								...prev,
								targetVaultManager: value,
							}))
						}
					/>
				</div>

				<div className="flex flex-col gap-sm">
					<label className="typo-label-xs text-text-secondary">
						Deposit assets
					</label>
					<RadioGroup.Default
						variant="card"
						size="md"
						orientation="horizontal"
						className="flex-wrap gap-sm"
						items={depositAssetOptions.map((option) => ({
							id: option.value,
							value: option.value,
							label: (
								<div className="flex items-center gap-xs">
									{option.value !== 'All' && (
										<MarketIcon
											className="h-4 w-4"
											marketSymbol={option.label}
										/>
									)}
									<span>{option.label}</span>
								</div>
							),
						}))}
						value={settings.targetDepositAsset}
						onValueChange={(value) =>
							setSettings((prev) => ({
								...prev,
								targetDepositAsset: value,
							}))
						}
					/>
				</div>

				<div className="flex flex-col gap-sm">
					<label className="typo-label-xs text-text-secondary">APY</label>
					<RadioGroup.Default
						variant="card"
						size="md"
						className="gap-sm"
						orientation="horizontal"
						items={apyOptions.map((option) => ({
							...option,
							id: option.value,
						}))}
						value={settings.targetApyPeriod}
						onValueChange={(value) =>
							setSettings((prev) => ({ ...prev, targetApyPeriod: value }))
						}
					/>
				</div>
			</SideDrawer.Body>
			<SideDrawer.Footer>
				<Button
					size="lg"
					variant="primary"
					className="w-full"
					onClick={handleApply}
				>
					Apply
				</Button>
			</SideDrawer.Footer>
		</SideDrawer.Container>
	);
};

export default VaultDisplayFiltersDrawer;
